import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { useAuth } from '@/hooks/useAuth';
import {
  CONSENT_CHANNELS,
  CONSENT_PURPOSES,
  consentPurposeMeta,
  fetchConsentStats,
  listConsentLog,
  listConsents,
  setConsent,
  type CoreConsent,
  type CoreConsentLog,
} from '@/lib/coreConsent';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function ConsentsTab({ org }: Props) {
  const { user } = useAuth();
  const userId = user?.id ?? 0;
  const [consents, setConsents] = useState<CoreConsent[]>([]);
  const [log, setLog] = useState<CoreConsentLog[]>([]);
  const [stats, setStats] = useState({ granted: 0, revoked: 0, total: 0, byPurpose: {} as Record<string, boolean> });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [c, l, s] = await Promise.all([listConsents(userId), listConsentLog(userId), fetchConsentStats(userId)]);
      setConsents(c);
      setLog(l);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (purpose: string, granted: boolean, channel: string) => {
    if (!userId) return;
    setBusy(purpose);
    try {
      await setConsent({ organizationId: org.id, tenantId: org.tenant_id, userId, purpose, granted, channel, source: 'dashboard' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    } finally {
      setBusy(null);
    }
  };

  const stateFor = (purpose: string): boolean => {
    const row = consents.find((c) => c.purpose === purpose);
    if (row) return !!row.granted;
    return !!consentPurposeMeta(purpose).defaultOptIn;
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Consentements centralisés</span> : marketing, notifications, communications,
        traitement de certaines données et partage avec certains services. Un utilisateur
        <span className="font-medium text-foreground-600"> n’est jamais considéré comme consentant à tout par défaut</span>.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.granted}</p>
          <p className="text-xs text-foreground-500">Consentis</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-close-circle-line text-lg text-foreground-400"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.revoked}</p>
          <p className="text-xs text-foreground-500">Non consentis</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-list-check-2 text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Finalités</p>
        </div>
      </div>

      {/* Finalités */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-shield-user-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Finalités de consentement</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {CONSENT_PURPOSES.map((p) => {
              const granted = stateFor(p.key);
              return (
                <div key={p.key} className="flex items-center gap-3 px-4 py-3">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${granted ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}><i className={p.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800">{p.label}</p>
                    <p className="text-[11px] text-foreground-400">{p.desc}{!p.defaultOptIn && !consents.find((c) => c.purpose === p.key) ? ' · opt-in requis' : ''}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {p.channels.map((ch) => {
                        const cm = CONSENT_CHANNELS.find((x) => x.key === ch);
                        return <span key={ch} className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500 flex items-center gap-0.5"><i className={cm?.icon}></i>{cm?.label}</span>;
                      })}
                    </div>
                  </div>
                  {busy === p.key && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                  <button
                    onClick={() => toggle(p.key, !granted, p.channels[0])}
                    disabled={busy === p.key}
                    className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${granted ? 'bg-primary-500' : 'bg-background-200'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${granted ? 'left-[26px]' : 'left-0.5'}`}></span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Journal */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Journal des consentements</h4>
        </div>
        {log.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun changement enregistré.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[320px] overflow-y-auto">
            {log.map((l) => {
              const meta = consentPurposeMeta(l.purpose);
              const granted = l.action === 'granted';
              return (
                <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${granted ? 'bg-primary-100 text-primary-700' : 'bg-red-100 text-red-700'}`}>
                    <i className={granted ? 'ri-check-line' : 'ri-close-line'}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground-800 truncate">{meta.label} — {granted ? 'accordé' : 'retiré'}</p>
                    <p className="text-[11px] text-foreground-400 truncate">{l.source || '—'}{l.channel ? ` · ${l.channel}` : ''} · {l.created_at ? new Date(l.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}